import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import "../styles/AdminDeposits.css";

const API_BASE_URL = "https://growrise.shop";

export default function AdminWithdrawals() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [processingId, setProcessingId] = useState("");

  const getToken = () => {
    return (
      localStorage.getItem("token") ||
      localStorage.getItem("naxoraToken") ||
      ""
    );
  };

  const getHeaders = (includeJson = false) => {
    const token = getToken();
    const headers = {};

    if (includeJson) {
      headers["Content-Type"] = "application/json";
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  };

  const parseApiResponse = async (response) => {
    const rawText = await response.text();

    let data = null;

    try {
      data = rawText ? JSON.parse(rawText) : null;
    } catch (error) {
      data = null;
    }

    return {
      ok: response.ok,
      status: response.status,
      data,
      rawText,
    };
  };

  const getApiErrorMessage = (parsed, fallbackMessage) => {
    if (parsed?.data?.message) {
      return parsed.data.message;
    }

    if (parsed?.rawText) {
      if (
        parsed.rawText.includes("<!DOCTYPE") ||
        parsed.rawText.includes("<html")
      ) {
        return `Server returned HTML instead of JSON (HTTP ${parsed.status})`;
      }

      return parsed.rawText;
    }

    return fallbackMessage;
  };

  const handleUnauthorized = async () => {
    await Swal.fire({
      icon: "error",
      title: "Unauthorized",
      text: "Admin access required",
      confirmButtonColor: "#d4af37",
      background: "#0b0b0b",
      color: "#ffffff",
    });

    localStorage.clear();
    window.location.replace("/login");
  };

  const fetchWithdrawals = async () => {
    try {
      setLoading(true);

      const res = await fetch(`${API_BASE_URL}/api/withdrawals/admin/all`, {
        method: "GET",
        headers: getHeaders(),
      });

      const parsed = await parseApiResponse(res);

      if (res.status === 401 || res.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!parsed.ok) {
        throw new Error(
          getApiErrorMessage(parsed, "Failed to load withdrawals")
        );
      }

      setWithdrawals(parsed.data?.withdrawals || []);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message || "Failed to load withdrawals",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWithdrawals();
  }, []);

  const filtered = useMemo(() => {
    const keyword = search.toLowerCase().trim();

    return withdrawals.filter((item) => {
      const matchSearch =
        !keyword ||
        item.user?.name?.toLowerCase().includes(keyword) ||
        item.user?.email?.toLowerCase().includes(keyword) ||
        item.walletAddress?.toLowerCase().includes(keyword) ||
        item.id?.toLowerCase().includes(keyword);

      const matchStatus =
        statusFilter === "all" ||
        item.status?.toLowerCase() === statusFilter;

      return matchSearch && matchStatus;
    });
  }, [withdrawals, search, statusFilter]);

  const approve = async (id) => {
    try {
      setProcessingId(id);

      const { value: adminNote } = await Swal.fire({
        title: "Approve Withdrawal",
        input: "text",
        inputLabel: "Admin Note (optional)",
        inputPlaceholder: "Enter note",
        showCancelButton: true,
        confirmButtonText: "Approve",
        confirmButtonColor: "#d4af37",
        cancelButtonColor: "#6c757d",
        background: "#0b0b0b",
        color: "#ffffff",
      });

      if (adminNote === undefined) {
        setProcessingId("");
        return;
      }

      const res = await fetch(
        `${API_BASE_URL}/api/withdrawals/admin/approve/${id}`,
        {
          method: "PUT",
          headers: getHeaders(true),
          body: JSON.stringify({
            adminNote: adminNote || "",
          }),
        }
      );

      const parsed = await parseApiResponse(res);

      if (res.status === 401 || res.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!parsed.ok) {
        throw new Error(getApiErrorMessage(parsed, "Failed to approve withdrawal"));
      }

      await Swal.fire({
        icon: "success",
        title: "Approved",
        text: parsed.data?.message || "Withdrawal approved successfully",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });

      fetchWithdrawals();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to approve withdrawal",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setProcessingId("");
    }
  };

  const reject = async (id) => {
    try {
      setProcessingId(id);

      const { value: adminNote } = await Swal.fire({
        title: "Reject Withdrawal",
        input: "text",
        inputLabel: "Reason / Admin Note",
        inputPlaceholder: "Enter rejection reason",
        showCancelButton: true,
        confirmButtonText: "Reject",
        confirmButtonColor: "#d4af37",
        cancelButtonColor: "#6c757d",
        background: "#0b0b0b",
        color: "#ffffff",
      });

      if (adminNote === undefined) {
        setProcessingId("");
        return;
      }

      const res = await fetch(
        `${API_BASE_URL}/api/withdrawals/admin/reject/${id}`,
        {
          method: "PUT",
          headers: getHeaders(true),
          body: JSON.stringify({
            adminNote: adminNote || "",
          }),
        }
      );

      const parsed = await parseApiResponse(res);

      if (res.status === 401 || res.status === 403) {
        await handleUnauthorized();
        return;
      }

      if (!parsed.ok) {
        throw new Error(getApiErrorMessage(parsed, "Failed to reject withdrawal"));
      }

      await Swal.fire({
        icon: "success",
        title: "Rejected",
        text: parsed.data?.message || "Withdrawal rejected successfully",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });

      fetchWithdrawals();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Failed to reject withdrawal",
        confirmButtonColor: "#d4af37",
        background: "#0b0b0b",
        color: "#ffffff",
      });
    } finally {
      setProcessingId("");
    }
  };

  return (
    <div className="admin-deposits-page">
      <h2>Withdraw Requests</h2>

      <div style={{ margin: "15px 0", display: "flex", gap: "10px", flexWrap: "wrap" }}>
        <input
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <table className="admin-deposits-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Requested</th>
              <th>Wallet</th>
              <th>Status</th>
              <th>Note</th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan="7">No withdrawal requests found</td>
              </tr>
            ) : (
              filtered.map((item) => (
                <tr key={item.id}>
                  <td>{item.user?.name || "-"}</td>
                  <td>{item.user?.email || "-"}</td>
                  <td>${Number(item.requestedAmount || item.amount || 0).toFixed(2)}</td>
                  <td>{item.walletAddress || "-"}</td>
                  <td>{item.status || "-"}</td>
                  <td>{item.adminNote || item.note || "-"}</td>

                  <td>
                    {item.status === "pending" ? (
                      <>
                        <button
                          onClick={() => approve(item.id)}
                          disabled={processingId === item.id}
                        >
                          {processingId === item.id ? "Processing..." : "Approve"}
                        </button>

                        <button
                          onClick={() => reject(item.id)}
                          disabled={processingId === item.id}
                        >
                          {processingId === item.id ? "Processing..." : "Reject"}
                        </button>
                      </>
                    ) : (
                      <span>No Action</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}